import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Vehicle {
  id: string;
  company_id: string;
  vehicle_type_id: string;
  plate_number: string;
  vin: string | null;
  brand: string | null;
  model: string | null;
  year: number | null;
  is_available: boolean;
  last_maintenance_date: string | null;
  next_maintenance_date: string | null;
  registration_expiry?: string | null;
  registration_verified_at?: string | null;
  registration_first_date?: string | null;
  created_at: string;
  updated_at: string;
  vehicle_types?: any;
}

export const useVehicles = () => {
  const { data: vehicles = [], isLoading, error } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Non authentifié');

        const { data, error } = await supabase
          .from('vehicles')
          .select(`
            *,
            vehicle_types (
              name,
              class
            )
          `)
          .order('plate_number');

        if (error) throw error;
        return data as Vehicle[];
      } catch (err) {
        toast.error('Erreur lors du chargement des véhicules');
        throw err;
      }
    },
  });

  const availableVehicles = vehicles.filter(v => v.is_available);

  return {
    vehicles,
    availableVehicles,
    isLoading,
    error,
  };
};

export const useAvailableVehiclesForMission = (pickupDate?: string, deliveryDate?: string) => {
  const queryClient = useQueryClient();
  
  const { data: availableVehicles = [], isLoading } = useQuery({
    queryKey: ['available-vehicles-for-mission', pickupDate, deliveryDate],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Non authentifié');

        // Get all vehicles with their types
        const { data: vehicles, error: vehiclesError } = await supabase
          .from('vehicles')
          .select(`
            *,
            vehicle_types (
              name,
              class
            )
          `)
          .order('plate_number');

        if (vehiclesError) throw vehiclesError;

        if (!pickupDate || !deliveryDate) {
          return vehicles?.filter(v => v.is_available) || [];
        }

        // Get all active transport jobs
        const { data: jobs, error: jobsError } = await supabase
          .from('transport_jobs')
          .select(`
            vehicle_id,
            orders (
              pickup_date,
              delivery_date,
              status
            )
          `)
          .not('vehicle_id', 'is', null)
          .is('completed_at', null)
          .is('declined_at', null);

        if (jobsError) throw jobsError;

        const newPickup = new Date(pickupDate);
        const newDelivery = new Date(deliveryDate);

        return vehicles?.filter(vehicle => {
          // Block vehicles with missing or expired registration card (verified by OCR)
          const v: any = vehicle;
          if (!v.registration_verified_at) return false;
          if (!v.registration_expiry) return false;
          if (new Date(v.registration_expiry) <= newDelivery) return false;

          // Get all active jobs for this vehicle
          const vehicleJobs = jobs?.filter(job => job.vehicle_id === vehicle.id) || [];
          if (vehicleJobs.length === 0) return true;

          const hasConflict = vehicleJobs.some(job => {
            if (!job.orders) return false;
            const jobPickup = new Date(job.orders.pickup_date);
            const jobDelivery = new Date(job.orders.delivery_date);
            return !(newDelivery <= jobPickup || newPickup >= jobDelivery);
          });

          return !hasConflict;
        }) || [];
      } catch (err) {
        toast.error('Erreur lors du chargement des véhicules disponibles');
        throw err;
      }
    },
    enabled: !!pickupDate && !!deliveryDate,
  });

  return {
    availableVehicles,
    isLoading,
  };
};

export const useAddVehicle = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: addVehicle, isPending: isAdding } = useMutation({
    mutationFn: async (vehicleData: {
      vehicle_type_id: string;
      plate_number: string;
      vin: string | null;
      brand: string | null;
      model: string | null;
      year: number | null;
      last_maintenance_date: string | null;
      next_maintenance_date: string | null;
    }) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Non authentifié');

        // Get user's company_id
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;
        if (!userData?.company_id) throw new Error('Aucune société associée');

        const { data, error } = await supabase
          .from('vehicles')
          .insert({
            ...vehicleData,
            company_id: userData.company_id,
          })
          .select()
          .single();

        if (error) throw error;
        return data.id; // Return vehicle ID
      } catch (err) {
        toast.error('Erreur lors de l\'ajout du véhicule');
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Véhicule ajouté avec succès');
    },
  });

  return { addVehicle, isAdding };
};

export const useUpdateVehicle = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: updateVehicle, isPending: isUpdating } = useMutation({
    mutationFn: async ({ id, ...vehicleData }: {
      id: string;
      vehicle_type_id?: string;
      plate_number?: string;
      vin?: string | null;
      brand?: string | null;
      model?: string | null;
      year?: number | null;
      is_available?: boolean;
      last_maintenance_date?: string | null;
      next_maintenance_date?: string | null;
    }) => {
      try {
        const { data, error } = await supabase
          .from('vehicles')
          .update(vehicleData)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } catch (err) {
        toast.error('Erreur lors de la mise à jour du véhicule');
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Véhicule mis à jour avec succès');
    },
  });

  return { updateVehicle, isUpdating };
};

export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: deleteVehicle, isPending: isDeleting } = useMutation({
    mutationFn: async (vehicleId: string) => {
      try {
        const { error } = await supabase
          .from('vehicles')
          .delete()
          .eq('id', vehicleId);

        if (error) throw error;
      } catch (err) {
        toast.error('Erreur lors de la suppression du véhicule');
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Véhicule supprimé avec succès');
    },
  });

  return { deleteVehicle, isDeleting };
};
